import { cloneElement, isValidElement, useId, type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { cn } from '../utils/cn.js';

export interface FieldProps extends HTMLAttributes<HTMLLabelElement> {
  /** Visible field label. */
  label: ReactNode;
  /** Optional helper text below the input. Hidden when an error is showing. */
  helper?: ReactNode;
  /** Error text. Occupies the same slot as helper — layout stays stable. */
  error?: ReactNode;
  /** Mark the field visually as required. */
  required?: boolean;
  /** Mark the field visually as optional (e.g. `(optional)`). Mutually exclusive with `required`. */
  optional?: boolean;
  /** Optional inline element rendered opposite the label (e.g. a "Forgot?" link on a password field). */
  labelAction?: ReactNode;
  /**
   * The input element. If you pass a native <input>/<select>/<textarea> Field will
   * auto-wire `id`, `aria-invalid`, and `aria-describedby` for you.
   */
  children: ReactElement | ReactNode;
  /** Override the auto-generated id. */
  id?: string;
}

/**
 * `<Field>` wraps every label + input + help + error pattern into one
 * primitive with correct a11y wiring. Replaces the
 * `<label className="grid gap-1.5"><span className="label">…</span><input …/></label>`
 * copy-paste in every public form.
 *
 * Rules from docs/design/system/accessibility.md:
 *   - label above input (never floating)
 *   - 8px gap label→input, 6px input→helper
 *   - reserved 20px error slot to prevent layout shift
 *   - aria-invalid on the input when in error
 *   - aria-describedby linked to helper + error slot ids
 *   - error text in role="alert" for AT announcements
 */
export function Field({
  label,
  helper,
  error,
  required,
  optional,
  labelAction,
  children,
  id: propId,
  className,
  ...rest
}: FieldProps) {
  const autoId = useId();
  const id = propId ?? autoId;
  const helperId = `${id}-help`;
  const errorId = `${id}-error`;

  // Auto-wire the child input if it's a native form element.
  const wiredChild = isValidElement(children)
    ? cloneElement(children as ReactElement<{ id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }>, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': [helper ? helperId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined,
      })
    : children;

  return (
    <label className={cn('grid gap-1.5', className)} htmlFor={id} {...rest}>
      <span className="flex justify-between items-baseline gap-2">
        <span className="label">
          {label}
          {required && <span aria-hidden="true" className="ml-1 text-ink-danger">*</span>}
          {optional && (
            <span className="ml-1 text-ink-tertiary text-xs font-normal">(optional)</span>
          )}
        </span>
        {labelAction}
      </span>
      {wiredChild}
      {/* Reserved slot: shows helper OR error, min-height so layout is stable. */}
      <span className="helper" style={{ minHeight: 20 }}>
        {error ? (
          <span id={errorId} role="alert" style={{ color: 'rgb(var(--ink-danger))' }}>
            {error}
          </span>
        ) : helper ? (
          <span id={helperId}>{helper}</span>
        ) : null}
      </span>
    </label>
  );
}
